import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { setSessionId, setUserId, setUserName, setIsAdmin, clearSession } from '../Redux/action/sessionAction';
import axios from 'axios';

const SessionManager = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const sessionId = useSelector((state) => state.session.sessionId);

  useEffect(() => {
    // Clean up any oversized localStorage on component mount
    const cleanupLocalStorage = () => {
      try {
        const storedState = localStorage.getItem('sessionState');
        if (storedState && storedState.length > 5 * 1024 * 1024) { // 5MB limit
          console.warn('Found oversized localStorage data, cleaning up...');
          localStorage.removeItem('sessionState');
          return true; // Indicate cleanup happened
        }
        return false;
      } catch (error) {
        console.warn('Error checking localStorage, clearing it:', error);
        localStorage.removeItem('sessionState');
        return true;
      }
    };

    const validateSession = async () => {
      // Clean up localStorage first
      const wasCleanedUp = cleanupLocalStorage();
      
      // Skip validation for authentication pages and public share routes
      const authPages = ['/authentication/sign-in', '/authentication/sign-up', '/authentication/forgot-password'];
      const isShareRoute = location.pathname.startsWith('/share/');
      
      if (authPages.includes(location.pathname) || isShareRoute) {
        return;
      }

      // If no session in Redux, try to restore from localStorage
      if (!sessionId) {
        // Try new format first (sessionState object)
        let storedSessionId, storedUserId, storedUserName, storedIsAdmin;
        
        try {
          const sessionState = localStorage.getItem('sessionState');
          if (sessionState) {
            const parsedState = JSON.parse(sessionState);
            storedSessionId = parsedState.session?.sessionId || parsedState.sessionId;
            storedUserId = parsedState.session?.setUserId || parsedState.setUserId;
            storedUserName = parsedState.session?.setUserName || parsedState.setUserName;
            storedIsAdmin = parsedState.session?.isAdmin || parsedState.isAdmin || false;
          }
        } catch (error) {
          console.error('Error parsing sessionState:', error);
        }

        // Fallback to old format (individual items) if new format not found
        if (!storedSessionId) {
          storedSessionId = localStorage.getItem('sessionId');
          const userIdStr = localStorage.getItem('userId');
          const userNameStr = localStorage.getItem('userName');
          
          if (userIdStr) {
            try {
              storedUserId = JSON.parse(userIdStr);
            } catch (e) {
              storedUserId = userIdStr;
            }
          }
          
          if (userNameStr) {
            try {
              storedUserName = JSON.parse(userNameStr);
            } catch (e) {
              storedUserName = userNameStr;
            }
          }
        }

        if (storedSessionId && storedUserId && storedUserName && !wasCleanedUp) {
          try {
            // Validate session with server
            const apiUrl = "https://seltentechnologiesdemo.com/moona-backend/service/v4_1/rest.php";
            const method = "get_entry_list";
            const input_type = "JSON";
            const response_type = "JSON";
            const rest_data = JSON.stringify({
              session: storedSessionId,
              module_name: "Users",
              max_results: 1,
            });

            const fullUrl = `${apiUrl}?method=${method}&input_type=${input_type}&response_type=${response_type}&rest_data=${encodeURIComponent(rest_data)}`;
            
            const response = await axios.get(fullUrl);
            
            if (response.data && response.data.name !== "Invalid Session ID") {
              // Session is still valid, restore to Redux
              dispatch(setSessionId(storedSessionId));
              dispatch(setUserId(storedUserId));
              dispatch(setUserName(storedUserName));
              dispatch(setIsAdmin(storedIsAdmin));
            } else {
              // Session is invalid, clear everything
              localStorage.removeItem('sessionState');
              localStorage.removeItem('sessionId');
              localStorage.removeItem('userId');
              localStorage.removeItem('userName');
              dispatch(clearSession());
              const isShareRoute = location.pathname.startsWith('/share/');
              if (location.pathname !== '/authentication/sign-in' && !isShareRoute) {
                navigate('/authentication/sign-in');
              }
            }
          } catch (error) {
            console.error('Session validation failed:', error);
            // On error, clear session and redirect to login
            localStorage.removeItem('sessionState');
            localStorage.removeItem('sessionId');
            localStorage.removeItem('userId');
            localStorage.removeItem('userName');
            dispatch(clearSession());
            const isShareRoute = location.pathname.startsWith('/share/');
            if (location.pathname !== '/authentication/sign-in' && !isShareRoute) {
              navigate('/authentication/sign-in');
            }
          }
        } else {
          // No stored session or data was cleaned up, redirect to login for protected routes
          const isShareRoute = location.pathname.startsWith('/share/');
          if (location.pathname !== '/authentication/sign-in' && location.pathname !== '/authentication/sign-up' && location.pathname !== '/authentication/forgot-password' && !isShareRoute) {
            navigate('/authentication/sign-in');
          }
        }
      }
    };

    validateSession();
  }, [sessionId, location.pathname, dispatch, navigate]);

  return children;
};

export default SessionManager;